import axios from "axios";
import { apiRoot } from "../../index";
import { useCallback, useContext, useEffect, useState } from "react";
import { UserContext } from "../../App";

import { startLoading, stopLoading } from "../loader";

export async function createAgents(token, data) {
  startLoading();
  try {
    const res = await axios.post(
      `${apiRoot}/api/v1/agents/create`,
      {
        twilioPhoneNumber: data.twilioPhoneNumber,
        twilioAccountSID: data.twilioAccountSID,
        twilioAuthToken: data.twilioAuthToken,
        label: data.label,
        businessName: data.businessName,
        prompt: data.prompt,
      },
      {
        headers: {
          Authorization: token,
        },
      }
    )
    return res.data;
  } catch (error) {
    console.error("Error creating agent:", error);
    throw error;
  } finally {
    stopLoading();
  }

}

export async function getAgents(token) {
  startLoading();

  const res = await axios.get(`${apiRoot}/api/v1/agents`, {
    headers: {
      Authorization: token,
    },
  });

  stopLoading();

  return res.data;
}

export async function getAgentById(token, agentId) {
  startLoading();
  const res = await axios.get(`${apiRoot}/api/v1/agents/${agentId}`, {
    headers: {
      Authorization: token,
    },
  });
  stopLoading();
  return res.data;
}

export async function getCallDetailsByDetails(token) {
  startLoading();

  const res = await axios.get(`${apiRoot}/api/v1/calls/by-phone`, {
    headers: {
      Authorization: token,
    },
  });

  stopLoading();

  return res.data;
}

export async function deleteAgent(token, agentId) {
  startLoading();
  try {
    const res = await axios.post(
      `${apiRoot}/api/v1/agent/delete`,
      { agentId },
      {
        headers: {
          Authorization: token,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error in deleting an agent:", error);
    throw error;
  } finally {
    stopLoading();
  }
}

export async function editAgent(token, agentId, updatedData) {
  startLoading()
  try {
    const res = await axios.put(
      `${apiRoot}/api/v1/agents/edit`,
      { agentId, updatedData },
      {
        headers: {
          Authorization: token,
        },
      }
    );
    return res.data;
  } catch ( error ) {
    console.error("Error in editing an agent:", error);
    throw error;
  } finally {
    stopLoading();
  }
}




export function useAgents() {
  const { userInfos } = useContext(UserContext);
  const [agents, setAgents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function fetchAgents() {
      try {
        const data = await getAgents(userInfos.jwt);
        setAgents(data);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // No agents found
          setAgents([]);
        } else {
          console.error("Error in useAgents:", error.response || error.message);
        }
      } finally {
        setLoaded(true); // Ensure spinner stops
      }
    }

    if (userInfos.jwt) {
      fetchAgents();
    }
  }, [userInfos.jwt]);

  const getAgentByIdWithToken = useCallback(
    async (agentId) => {
      if (!userInfos.jwt) {
        throw new Error("User is not authenticated");
      }
      return await getAgentById(userInfos.jwt, agentId);
    },
    [userInfos.jwt]
  );

  const deleteAgentByIdWithToken = useCallback(
    async (agentId) => {
      if (!userInfos.jwt) {
        throw new Error("User is not authenticated");
      }
      await deleteAgent(userInfos.jwt, agentId);
      setAgents((prevAgents) => {
        if (Array.isArray(prevAgents)) {
          return prevAgents.filter((agent) => agent.id !== agentId);
        }
        const updatedAgents = { ...prevAgents };
        delete updatedAgents[agentId];
        return updatedAgents;
      });
    },
    [userInfos.jwt]
  );

  const editAgentByIdWithToken = useCallback(
    async (agentId, updatedData) => {
      if (!userInfos.jwt) {
        throw new Error("User is not authenticated");
      }
      await editAgent(userInfos.jwt, agentId, updatedData);
      setAgents((prevAgents) => {
        if (Array.isArray(prevAgents)) {
          return prevAgents.map((agent) =>
            agent.id === agentId ? { ...agent, ...updatedData } : agent
          );
        }
        const updatedAgents = { ...prevAgents };
        updatedAgents[agentId] = {
          ...updatedAgents[agentId],
          ...updatedData,
        };
        return updatedAgents;
      });
    },
    [userInfos.jwt]
  );

  async function deleteCall(token, callId) {
    startLoading();
    try {
      const res = await axios.delete(`${apiRoot}/api/v1/delete-call/${callId}`, {
        headers: {
          Authorization: token,
        },
      });
      return res.data;
    } catch (error) {
      console.error("Error deleting call:", error);
      throw error;
    } finally {
      stopLoading();
    }
  }

  const deleteCallByIdWithToken = useCallback(
    async (callId) => {
      if (!userInfos.jwt) {
        throw new Error("User is not authenticated");
      }
      await deleteCall(userInfos.jwt, callId);
      setAgents((prevAgents) => {
        const agentsArray = Array.isArray(prevAgents)
          ? prevAgents
          : Object.keys(prevAgents).map((key) => ({
              id: key,
              ...prevAgents[key],
            }));

        return agentsArray.map((agent) => ({
          ...agent,
          calls: agent.calls.filter((call) => call.id !== callId),
        }));
      });
    },
    [userInfos.jwt]
  );
  
  

  return { agents, loaded, getAgentByIdWithToken, deleteAgentByIdWithToken, editAgentByIdWithToken , deleteCallByIdWithToken};
}
